import React, { FC, useEffect } from 'react';

import {
  BookingState,
  counterOffer,
  ICalendarEventType,
  pharmacistActions,
  pharmacistEvent,
} from '@pharmaplan/common';
import ServiceHandler from '@pharmaplan/common/helpers/ServiceHandler';

import { Box, Typography } from '@mui/material';
import { ReactComponent as BonusBag } from '../../../assets/svg/bonusBag.svg';
import CustomButton from '../../common/CustomButton';
import {
  renderScreen,
  setBack,
  setButtons,
  setDrawerContentPadding,
  setFooterShow,
  setPagination,
} from '../../../actions/drawerActions';
import { selectFooterPaginationCurrent } from '../../../selectors/drawerSelector';
import { useAppSelector } from '../../../hooks/useAppSelector';
import { useAppDispatch } from '../../../hooks/useAppDispatch';
import { isPastServerDate } from '../../../helpers/Functions';
import useServiceWorkshiftDetailsEvent from '../../../hooks/serviceWorkshiftDetails/useServiceWorkshiftDetailsEvent';
import {
  Constants,
  ScreenTypes,
  stackedChipsColors,
} from '../../../helpers/Constants';
import useSelfService from '../../../hooks/useSelfService';
import PharmacyOperation from '../PharmacyOperation';
import StackedBox from '../../common/StackedBox';
import ContactDetails from '../ContactDetails';
import strings from '../../../localization';
import PharmacyCard from '../PharmacyCard';
import ServiceNote from '../ServiceNotes';
import ServiceCard from '../ServiceCard';
import { WorkshiftListSortOption } from '../../WorkshiftList/helper';

import LongTermWorkshiftDetails from './LongTermWorkshiftDetails';
import { showConfirmBox } from './helper';
import ConfirmBox from './ConfirmBox';
import classes from './styles';

export interface IServiceWorkshiftDetails {
  eventIds: Array<string>;
  eventType: ICalendarEventType;
  isAutoSyncIDMap?: Record<string, boolean>;
  workshiftListSort?: WorkshiftListSortOption;
}

const { None } = BookingState;
const { counterOfferBtn, counterOfferContainer } = classes;

const {
  variant: { text },
} = Constants;

const ServiceWorkshiftDetails: FC<IServiceWorkshiftDetails> = ({
  eventIds,
  eventType,
  isAutoSyncIDMap,
  workshiftListSort = WorkshiftListSortOption.shiftType,
}) => {
  const dispatch = useAppDispatch();
  const serviceType = ServiceHandler.getService();

  const {
    serviceConfig,
    goToCounterOffer,
    buttonConfig,
    isBooked,
    isWorkshift,
    isSelfWorkshift,
    isCounterOffer,
    isAutoSyncRecord,
  } = useServiceWorkshiftDetailsEvent({
    eventIds,
    eventType,
    isAutoSyncIDMap,
  });

  const { isSelfService } = useSelfService();
  const isSelfSyncedEvent = isSelfService && !isAutoSyncRecord;
  const isSelfSyncedWorkshift = isSelfWorkshift && !isAutoSyncRecord;

  const event = useAppSelector(pharmacistEvent);
  const current = useAppSelector(selectFooterPaginationCurrent);
  const counterOfferEvent = useAppSelector(counterOffer);

  const {
    notes,
    bookingState,
    startDate,
    longTermShiftContract,
    cost,
    shifts,
    totalCost,
    bookedShiftCount,
    isPromoBooking,
    counterShiftContract,
  } = event ?? {};
  const { longTermShiftContractId, allowanceNotes } = longTermShiftContract ?? {};
  const { notes: counterNotes } = counterOfferEvent ?? {};

  const displayNote = isCounterOffer ? counterNotes : notes;

  const BillDetails = serviceConfig[serviceType].billDetails;
  const { primaryButton, secondaryButton } = buttonConfig[eventType as keyof typeof buttonConfig] ?? {};

  const successAction = pharmacistActions.getPharmacistEvent;

  const handleButtonClick = () => {
    goToCounterOffer(eventIds);
  };

  const isPast = isPastServerDate(startDate);

  const previousScreen = () => {
    dispatch(
      renderScreen({
        screenType: ScreenTypes.serviceWorkshiftDetails,
        screenNumber: 1,
        eventIds,
        extraParameters: {
          isAutoSyncRecord,
          workshiftListSort,
        },
        type: eventType,
      }),
    );
  };

  const back = () => {
    dispatch(
      renderScreen({
        screenType: ScreenTypes.workshiftList,
        screenNumber: 1,
        date: startDate,
        type: eventType,
        extraParameters: {
          workshiftListSort,
        },
      }),
    );
  };

  useEffect(() => {
    if (isBooked) {
      dispatch(setBack(null));
    }
    if (isWorkshift) {
      dispatch(setDrawerContentPadding('16px'));
      dispatch(setBack(back));
      dispatch(setFooterShow(true));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isBooked, startDate]);

  useEffect(() => {
    dispatch(
      setButtons({
        show: !isPast,
        primaryButton: {
          variant: 'contained',
          ...primaryButton,
        },
        secondaryButton: {
          variant: 'text',
          ...secondaryButton,
        },
      }),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [buttonConfig, eventType, isPast]);

  useEffect(() => {
    dispatch(
      setPagination({
        show: true,
        total: eventIds.length,
        current,
      }),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventIds.length, current]);

  const contractValue = isBooked ? totalCost : cost;

  return (
    <>
      {showConfirmBox(bookingState ?? None, eventType) && <ConfirmBox />}

      <StackedBox
        sx={classes.stackedBox}
        showStacks={!!longTermShiftContractId}
        backgroundColor={
          stackedChipsColors[eventType as keyof typeof stackedChipsColors]
        }
      >
        <PharmacyCard
          successAction={successAction}
          event={event}
          showDiscountIcon={isPromoBooking}
          customContainerStyle={classes.customServiceCard}
          isPromoBooking={isPromoBooking}
          type={eventType}
          goBack={previousScreen}
          isAutoSyncRecord={isAutoSyncRecord}
          longTermShiftContract={longTermShiftContract}
        />
      </StackedBox>
      {isPromoBooking && (
        <ServiceCard
          customContainerStyle={classes.padTop}
          content={(
            <Box sx={classes.promoContainer}>
              <BonusBag height={26} width={26} />
              <Typography sx={classes.promo}>
                {strings.promoWasApplied}
              </Typography>
            </Box>
          )}
        />
      )}
      {longTermShiftContractId ? (
        <>
          {isBooked && BillDetails}
          <LongTermWorkshiftDetails
            type={eventType}
            shifts={shifts ?? []}
            contractValue={contractValue ?? 0}
            allowances={allowanceNotes}
            bookedShiftCount={bookedShiftCount}
            proposedAllowances={counterShiftContract?.allowanceNotes}
          />
        </>
      ) : (
        <>
          {BillDetails}
          {isSelfSyncedWorkshift && (
            <CustomButton
              variant={text}
              label={`${strings.submitCounterOffer}?`}
              onClick={handleButtonClick}
              customButtonStyle={counterOfferBtn}
              customClass={counterOfferContainer}
            />
          )}
        </>
      )}

      {(isBooked || isSelfSyncedEvent) && (
        <ContactDetails successAction={successAction} />
      )}
      <PharmacyOperation successAction={successAction} event={event} />
      {displayNote && <ServiceNote notes={displayNote} />}
    </>
  );
};

export default ServiceWorkshiftDetails;
